// v4.3.0 — PageZoomOverlay : zoom pincer / double-tap / molette sur une page
// de journal (lecture des petits caractères). Pan au doigt quand zoomé.

import { useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut } from "lucide-react";

const MIN_SCALE = 1;
const MAX_SCALE = 5;

export default function PageZoomOverlay({ imageUrl, title, pageNumber, onClose }) {
  const [t, setT] = useState({ scale: 1, x: 0, y: 0 });
  const pointers = useRef(new Map());
  const pinchStart = useRef(null); // { dist, scale, midX, midY, x, y }
  const panStart = useRef(null);   // { px, py, x, y }
  const lastTap = useRef({ time: 0, x: 0, y: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function clampScale(s) { return Math.min(MAX_SCALE, Math.max(MIN_SCALE, s)); }

  function zoomAt(cx, cy, nextScale) {
    setT((prev) => {
      const s = clampScale(nextScale);
      if (s === 1) return { scale: 1, x: 0, y: 0 };
      // Garde le point (cx, cy) stable pendant le zoom.
      const ratio = s / prev.scale;
      return {
        scale: s,
        x: cx - ratio * (cx - prev.x),
        y: cy - ratio * (cy - prev.y),
      };
    });
  }

  function zoomBy(cx, cy, factor) {
    setT((prev) => {
      const s = clampScale(prev.scale * factor);
      if (s === 1) return { scale: 1, x: 0, y: 0 };
      const ratio = s / prev.scale;
      return {
        scale: s,
        x: cx - ratio * (cx - prev.x),
        y: cy - ratio * (cy - prev.y),
      };
    });
  }

  // Wheel natif non-passif : React root attache wheel en passive → preventDefault interdit.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheelNative = (e) => {
      e.preventDefault();
      zoomBy(e.clientX, e.clientY, e.deltaY < 0 ? 1.18 : 0.85);
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative);
  }, []);

  function onPointerDown(e) {
    e.preventDefault();
    // setPointerCapture peut jeter InvalidPointerId si le doigt est déjà levé.
    try { containerRef.current?.setPointerCapture?.(e.pointerId); } catch (_) {}
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinchStart.current = {
        dist,
        scale: t.scale,
        midX: (pts[0].x + pts[1].x) / 2,
        midY: (pts[0].y + pts[1].y) / 2,
        x: t.x, y: t.y,
      };
      panStart.current = null;
    } else if (pts.length === 1 && t.scale > 1) {
      panStart.current = { px: e.clientX, py: e.clientY, x: t.x, y: t.y };
    }
  }

  function onPointerMove(e) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2 && pinchStart.current) {
      const pc = pinchStart.current;
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const raw = pc.scale * (dist / pc.dist);
      const s = clampScale(raw);
      const ratio = s / pc.scale;
      const { midX, midY } = pc;
      setT({
        scale: s,
        x: s === 1 ? 0 : midX - ratio * (midX - pc.x),
        y: s === 1 ? 0 : midY - ratio * (midY - pc.y),
      });
    } else if (pts.length === 1 && panStart.current) {
      // Capture AVANT setT : la ref peut devenir null pendant le batch React.
      const ps = panStart.current;
      const nx = ps.x + (e.clientX - ps.px);
      const ny = ps.y + (e.clientY - ps.py);
      setT((prev) => ({ ...prev, x: nx, y: ny }));
    }
  }

  function onPointerUp(e) {
    const wasPinching = pointers.current.size === 2;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) panStart.current = null;

    // Double-tap : toggle zoom 1 ↔ 2.5 centré sur le tap.
    if (!wasPinching) {
      const now = Date.now();
      const dt = now - lastTap.current.time;
      const moved = Math.hypot(e.clientX - lastTap.current.x, e.clientY - lastTap.current.y);
      if (dt < 320 && moved < 30) {
        zoomAt(e.clientX, e.clientY, t.scale > 1.2 ? 1 : 2.5);
        lastTap.current = { time: 0, x: 0, y: 0 };
        return;
      }
      lastTap.current = { time: now, x: e.clientX, y: e.clientY };
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex flex-col"
      data-testid="page-zoom-overlay"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 text-white z-10">
        <div className="text-sm font-semibold truncate">
          {title} · Page {pageNumber}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => zoomBy(window.innerWidth / 2, window.innerHeight / 2, 0.7)} className="p-2 rounded-full hover:bg-white/10" data-testid="page-zoom-out">
            <ZoomOut size={19} />
          </button>
          <button onClick={() => zoomBy(window.innerWidth / 2, window.innerHeight / 2, 1.4)} className="p-2 rounded-full hover:bg-white/10" data-testid="page-zoom-in">
            <ZoomIn size={19} />
          </button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="page-zoom-close">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Zone zoomable */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden select-none"
        style={{ touchAction: "none", cursor: t.scale > 1 ? "grab" : "zoom-in" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid="page-zoom-canvas"
      >
        <img
          src={imageUrl}
          alt={`Page ${pageNumber}`}
          draggable={false}
          className="w-full h-full object-contain"
          style={{
            transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
            transformOrigin: "0 0",
            transition: pointers.current.size > 0 ? "none" : "transform 160ms ease-out",
          }}
        />
      </div>

      <div className="text-center text-[11px] text-white/40 pb-[calc(env(safe-area-inset-bottom)+10px)] pt-2">
        Pincez ou double-tapez pour zoomer · glissez pour naviguer
      </div>
    </div>
  );
}
